import React, { useState, useEffect } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { BaseUrl } from "../../BaseUrl";

export default function PrivacySettings({ doctor, onUpdate }) {
  const [loading, setLoading] = useState(false);
  const [privacySettings, setPrivacySettings] = useState({
    allowSearchEngineIndex: true,
    allowProfileDiscoveryByEmail: true,
    whoCanSendMessage: "anyone",
  });

  // Modal states
  const [showDeactivateModal, setShowDeactivateModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState("");
  const [password, setPassword] = useState("");

  // Fetch privacy settings from backend on mount
  useEffect(() => {
    const fetchPrivacySettings = async () => {
      try {
        const response = await axios.get(`${BaseUrl}/doctor/settings/privacy`, {
          withCredentials: true,
        });
        if (response.data?.success && response.data?.data) {
          setPrivacySettings({
            allowSearchEngineIndex:
              response.data.data.allowSearchEngineIndex ?? true,
            allowProfileDiscoveryByEmail:
              response.data.data.allowProfileDiscoveryByEmail ?? true,
            whoCanSendMessage: response.data.data.whoCanSendMessage || "anyone",
          });
        }
      } catch (error) {
        console.error("Error fetching privacy settings:", error);
        // Fall back to doctor data if available
        if (doctor?.privacySettings) {
          setPrivacySettings({
            allowSearchEngineIndex:
              doctor.privacySettings.allowSearchEngineIndex ?? true,
            allowProfileDiscoveryByEmail:
              doctor.privacySettings.allowProfileDiscoveryByEmail ?? true,
            whoCanSendMessage:
              doctor.privacySettings.whoCanSendMessage || "anyone",
          });
        }
      }
    };
    fetchPrivacySettings();
  }, [doctor]);

  const handleToggle = async (key) => {
    const newValue = !privacySettings[key];
    const newSettings = {
      ...privacySettings,
      [key]: newValue,
    };
    setPrivacySettings(newSettings);

    // Save to backend using correct endpoint
    try {
      await axios.put(
        `${BaseUrl}/doctor/settings/privacy/update`,
        { [key]: newValue },
        { withCredentials: true }
      );
      toast.success("Privacy setting updated");
    } catch (error) {
      console.error("Error updating privacy setting:", error);
      // Revert on error
      setPrivacySettings((prev) => ({
        ...prev,
        [key]: !newValue,
      }));
      toast.error(error.response?.data?.message || "Failed to update setting");
    }
  };

  const handleRadioChange = async (value) => {
    const oldValue = privacySettings.whoCanSendMessage;
    setPrivacySettings((prev) => ({
      ...prev,
      whoCanSendMessage: value,
    }));

    // Save to backend using correct endpoint
    try {
      await axios.put(
        `${BaseUrl}/doctor/settings/privacy/update`,
        { whoCanSendMessage: value },
        { withCredentials: true }
      );
      toast.success("Message preference updated");
    } catch (error) {
      console.error("Error updating message preference:", error);
      // Revert on error
      setPrivacySettings((prev) => ({
        ...prev,
        whoCanSendMessage: oldValue,
      }));
      toast.error(error.response?.data?.message || "Failed to update setting");
    }
  };

  const handleDeactivateAccount = async () => {
    if (!password) {
      toast.error("Please enter your password");
      return;
    }

    setLoading(true);
    try {
      await axios.post(
        `${BaseUrl}/doctor/settings/privacy/deactivate`,
        { password },
        { withCredentials: true }
      );

      toast.success("Account deactivated successfully");
      setShowDeactivateModal(false);
      setPassword("");

      // Log out user after deactivation
      setTimeout(() => {
        localStorage.clear();
        window.location.href = "/login";
      }, 2000);
    } catch (error) {
      toast.error(
        error.response?.data?.message || "Failed to deactivate account"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== "DELETE") {
      toast.error("Please type DELETE to confirm");
      return;
    }
    if (!password) {
      toast.error("Please enter your password");
      return;
    }

    setLoading(true);
    try {
      await axios.post(
        `${BaseUrl}/doctor/settings/privacy/schedule-deletion`,
        { password },
        { withCredentials: true }
      );

      toast.success("Account scheduled for deletion in 30 days");
      setShowDeleteModal(false);
      setDeleteConfirmText("");
      setPassword("");

      // Log out user after scheduling deletion
      setTimeout(() => {
        localStorage.clear();
        window.location.href = "/login";
      }, 2000);
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to delete account");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className='max-w-3xl'>
      <h2 className='mb-6 border-b border-gray-200 pb-3 text-2xl font-semibold text-gray-900'>
        Privacy Settings
      </h2>

      {/* Privacy Settings Section */}
      <div className='mb-8 border-b border-gray-200 pb-6'>
        <div className='space-y-6'>
          {/* Allow search engine to index your name */}
          <div>
            <div className='mb-2 flex items-center justify-between'>
              <div>
                <span className='text-gray-900'>
                  Allow search engine to index your facility
                </span>
                <p className='text-sm text-gray-500'>
                  Your facility will appear in Google and other search results
                </p>
              </div>
              <label className='relative inline-flex cursor-pointer items-center'>
                <input
                  type='checkbox'
                  checked={privacySettings.allowSearchEngineIndex}
                  onChange={() => handleToggle("allowSearchEngineIndex")}
                  className='peer sr-only'
                />
                <div className="peer h-6 w-11 rounded-full bg-gray-200 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-blue-600 peer-checked:after:translate-x-full peer-checked:after:border-white peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300"></div>
              </label>
            </div>
          </div>

          {/* Allow your profile to be discovered by email */}
          <div>
            <div className='flex items-center justify-between'>
              <div>
                <span className='text-gray-900'>
                  Allow profile to be discovered by email
                </span>
                <p className='text-sm text-gray-500'>
                  Others can find your facility using your email address
                </p>
              </div>
              <label className='relative inline-flex cursor-pointer items-center'>
                <input
                  type='checkbox'
                  checked={privacySettings.allowProfileDiscoveryByEmail}
                  onChange={() => handleToggle("allowProfileDiscoveryByEmail")}
                  className='peer sr-only'
                />
                <div className="peer h-6 w-11 rounded-full bg-gray-200 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:border after:border-gray-300 after:bg-white after:transition-all after:content-[''] peer-checked:bg-blue-600 peer-checked:after:translate-x-full peer-checked:after:border-white peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300"></div>
              </label>
            </div>
          </div>
        </div>
      </div>

      {/* Inbox Preferences Section */}
      <div className='mb-8 border-b border-gray-200 pb-6'>
        <h3 className='mb-4 text-lg font-semibold text-gray-900'>
          Message Preferences
        </h3>
        <div className='mb-4'>
          <p className='mb-4 text-gray-900'>Who can send you messages?</p>
          <div className='space-y-3'>
            <label className='flex cursor-pointer items-center'>
              <input
                type='radio'
                name='whoCanSendMessage'
                value='anyone'
                checked={privacySettings.whoCanSendMessage === "anyone"}
                onChange={() => handleRadioChange("anyone")}
                className='h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-500'
              />
              <span className='ml-3 text-gray-900'>
                Anyone (patients, partners, etc.)
              </span>
            </label>
            <label className='flex cursor-pointer items-center'>
              <input
                type='radio'
                name='whoCanSendMessage'
                value='followed'
                checked={privacySettings.whoCanSendMessage === "followed"}
                onChange={() => handleRadioChange("followed")}
                className='h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-500'
              />
              <span className='ml-3 text-gray-900'>Only my patients</span>
            </label>
            <label className='flex cursor-pointer items-center'>
              <input
                type='radio'
                name='whoCanSendMessage'
                value='noone'
                checked={privacySettings.whoCanSendMessage === "noone"}
                onChange={() => handleRadioChange("noone")}
                className='h-4 w-4 border-gray-300 text-blue-600 focus:ring-blue-500'
              />
              <span className='ml-3 text-gray-900'>No one</span>
            </label>
          </div>
        </div>
      </div>

      {/* Delete or Deactivate your Account Section */}
      <div className='mb-8'>
        <h3 className='mb-4 text-lg font-semibold text-gray-900'>
          Delete or Deactivate your Account
        </h3>
        <div className='space-y-4'>
          <div className='rounded-lg border border-yellow-200 bg-yellow-50 p-4'>
            <p className='mb-3 text-sm text-yellow-800'>
              <strong>Deactivate Account:</strong> Your account will be hidden
              but can be reactivated by logging in again.
            </p>
            <button
              onClick={() => setShowDeactivateModal(true)}
              className='text-sm text-yellow-700 underline hover:text-yellow-900'
            >
              Deactivate Account
            </button>
          </div>

          <div className='rounded-lg border border-red-200 bg-red-50 p-4'>
            <p className='mb-3 text-sm text-red-800'>
              <strong>Delete Account:</strong> Your account and all data will be
              permanently deleted after 30 days. This cannot be undone.
            </p>
            <button
              onClick={() => setShowDeleteModal(true)}
              className='text-sm text-red-700 underline hover:text-red-900'
            >
              Delete Account
            </button>
          </div>
        </div>
      </div>

      {/* Deactivate Modal */}
      {showDeactivateModal && (
        <div
          className='fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50'
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowDeactivateModal(false);
              setPassword("");
            }
          }}
        >
          <div className='mx-4 w-full max-w-md rounded-lg bg-white p-6'>
            <h3 className='mb-4 text-lg font-semibold text-yellow-700'>
              Deactivate Account
            </h3>
            <p className='mb-4 text-sm text-gray-600'>
              Are you sure you want to deactivate your account? Your profile
              will be hidden from other users but you can reactivate it anytime
              by logging in again.
            </p>
            <p className='mb-2 text-sm font-medium text-gray-700'>
              Enter your password to confirm:
            </p>
            <input
              type='password'
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className='mb-4 w-full rounded border border-gray-300 px-3 py-2 text-sm'
              placeholder='Your password'
            />
            <div className='flex justify-end gap-3'>
              <button
                onClick={() => {
                  setShowDeactivateModal(false);
                  setPassword("");
                }}
                className='rounded border border-gray-300 px-4 py-2 text-sm hover:bg-gray-50'
                disabled={loading}
              >
                Cancel
              </button>
              <button
                onClick={handleDeactivateAccount}
                disabled={loading}
                className='rounded bg-yellow-600 px-4 py-2 text-sm text-white hover:bg-yellow-700 disabled:opacity-50'
              >
                {loading ? "Deactivating..." : "Deactivate"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Modal */}
      {showDeleteModal && (
        <div
          className='fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50'
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowDeleteModal(false);
              setDeleteConfirmText("");
              setPassword("");
            }
          }}
        >
          <div className='mx-4 w-full max-w-md rounded-lg bg-white p-6'>
            <h3 className='mb-4 text-lg font-semibold text-red-700'>
              Delete Account Permanently
            </h3>
            <p className='mb-4 text-sm text-gray-600'>
              This action will schedule your account for permanent deletion in
              30 days. All your data including patients, orders, and records
              will be permanently deleted.
            </p>
            <p className='mb-2 text-sm font-medium text-gray-700'>
              Type <strong>DELETE</strong> to confirm:
            </p>
            <input
              type='text'
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              className='mb-4 w-full rounded border border-red-300 px-3 py-2 text-sm'
              placeholder='Type DELETE'
            />
            <p className='mb-2 text-sm font-medium text-gray-700'>
              Enter your password:
            </p>
            <input
              type='password'
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className='mb-4 w-full rounded border border-gray-300 px-3 py-2 text-sm'
              placeholder='Your password'
            />
            <div className='flex justify-end gap-3'>
              <button
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeleteConfirmText("");
                  setPassword("");
                }}
                className='rounded border border-gray-300 px-4 py-2 text-sm hover:bg-gray-50'
                disabled={loading}
              >
                Cancel
              </button>
              <button
                onClick={handleDeleteAccount}
                disabled={loading || deleteConfirmText !== "DELETE"}
                className='rounded bg-red-600 px-4 py-2 text-sm text-white hover:bg-red-700 disabled:opacity-50'
              >
                {loading ? "Deleting..." : "Delete Account"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
